import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";

type DragTarget = "left" | "right";

type UseVillageSatelliteTimelineArgs = {
  availableYears: number[];
  leftYear: number | null;
  rightYear: number | null;
  selectLeftYear: (year: number) => void;
  selectRightYear: (year: number) => void;
};

export type VillageSatelliteTimelineLabel = {
  year: number;
  percent: number;
  isLeft: boolean;
  isRight: boolean;
};

export function useVillageSatelliteTimeline({
  availableYears,
  leftYear,
  rightYear,
  selectLeftYear,
  selectRightYear,
}: UseVillageSatelliteTimelineArgs) {
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const [dragTarget, setDragTarget] = useState<DragTarget | null>(null);

  const maxIndex = Math.max(0, availableYears.length - 1);
  const leftIndex = leftYear != null ? Math.max(0, availableYears.indexOf(leftYear)) : 0;
  const rightIndex = rightYear != null ? Math.max(0, availableYears.indexOf(rightYear)) : maxIndex;

  const leftPercent = maxIndex > 0 ? (leftIndex / maxIndex) * 100 : 0;
  const rightPercent = maxIndex > 0 ? (rightIndex / maxIndex) * 100 : 100;

  const labels = useMemo<VillageSatelliteTimelineLabel[]>(
    () => availableYears.map((year, index) => ({
      year,
      percent: maxIndex > 0 ? (index / maxIndex) * 100 : 0,
      isLeft: year === leftYear,
      isRight: year === rightYear,
    })),
    [availableYears, leftYear, maxIndex, rightYear],
  );

  const moveMarkerFromClientX = useCallback((target: DragTarget, clientX: number) => {
    if (!timelineRef.current || maxIndex <= 0) {
      return;
    }

    const bounds = timelineRef.current.getBoundingClientRect();
    const normalized = Math.max(0, Math.min(1, (clientX - bounds.left) / bounds.width));
    const rawIndex = Math.round(normalized * maxIndex);

    if (target === "left") {
      const clamped = Math.max(0, Math.min(rawIndex, rightIndex - 1));
      const year = availableYears[clamped];
      if (typeof year === "number") {
        selectLeftYear(year);
      }
      return;
    }

    const clamped = Math.min(maxIndex, Math.max(rawIndex, leftIndex + 1));
    const year = availableYears[clamped];
    if (typeof year === "number") {
      selectRightYear(year);
    }
  }, [availableYears, leftIndex, maxIndex, rightIndex, selectLeftYear, selectRightYear]);

  useEffect(() => {
    if (!dragTarget) {
      return;
    }

    const currentDragTarget: DragTarget = dragTarget;

    function handlePointerMove(event: PointerEvent) {
      moveMarkerFromClientX(currentDragTarget, event.clientX);
    }

    function handlePointerUp() {
      setDragTarget(null);
    }

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [dragTarget, moveMarkerFromClientX]);

  const onTimelinePointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (!timelineRef.current) {
      return;
    }

    const midpoint = (leftPercent + rightPercent) / 2;
    const bounds = timelineRef.current.getBoundingClientRect();
    const pointerPercent = ((event.clientX - bounds.left) / bounds.width) * 100;
    const target: DragTarget = pointerPercent <= midpoint ? "left" : "right";
    moveMarkerFromClientX(target, event.clientX);
  }, [leftPercent, moveMarkerFromClientX, rightPercent]);

  const onMarkerPointerDown = useCallback((target: DragTarget, event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragTarget(target);
  }, []);

  return {
    timelineRef,
    dragTarget,
    leftIndex,
    rightIndex,
    leftPercent,
    rightPercent,
    labels,
    onTimelinePointerDown,
    onMarkerPointerDown,
  };
}
